import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUsIncomeStatementOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UsIncomeStatementRow = {
  id: number | null;
  datetime: string | null;
  type: string;
  incomeCategory: string;
  dataSource: string;
  account: string;
  direction: "inflow";
  quantity: number;
  asset: string;
  valueUSD: number;
  feesUSD: number;
  extTxnId: string | null;
};

export type UsIncomeStatementResponse = {
  taxYear: number;
  taxSetting: any;
  currency: string;
  totals: {
    totalValueUSD: number;
    totalEvents: number;
  };
  rows: UsIncomeStatementRow[];
};

export const useUsIncomeStatement = ({
  taxYear,
  enabled = true,
}: UseUsIncomeStatementOptions): UseQueryResult<UsIncomeStatementResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchIncomeStatement = async (): Promise<UsIncomeStatementResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/us/income-statement",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["us_income_statement", taxYear],
    queryFn: fetchIncomeStatement,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
